import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { PageLayout } from 'dibs-elements/exports/PageLayout';
import get from 'lodash.get';

import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer';
import { HpRespPersonalizationPanelCarousel } from './HpRespPersonalizationPanelCarousel';
import { HpRespPersonalizationPanel } from './HpRespPersonalizationPanel';
import { RenderIn, device } from '../../utils/RenderIn';

import styles from './HpRespPersonalizationModule.scss';
import { HpSharedCarouselTracking } from '../HpSharedCarouselTracking/HpSharedCarouselTracking';
import {
    trackPersonalizationImpression,
    trackPersonalizationMount,
} from './helpers/PersonalizationTracking';
import { filterPersonalizationItems } from './helpers/filterPersonalizationItems';

function HpRespPersonalizationModuleComponent({ userType, userId, personalization }) {
    const items = get(personalization, 'edges') || [];

    if (!userType) {
        return null;
    }

    return (
        <HpSharedModuleContainer addTopGap dataTn="personalization-wrapper">
            <PageLayout setViewportBackground>
                <HpSharedModuleContainer.Header>
                    <FormattedMessage
                        id="homepage.modules.personalizations.title"
                        defaultMessage="New Arrivals For You"
                    />
                </HpSharedModuleContainer.Header>
                <div className={styles.container}>
                    {items.length && userId ? (
                        <Fragment>
                            <RenderIn deviceTypes={[device.DESKTOP]}>
                                <HpRespPersonalizationPanelCarousel items={items} />
                            </RenderIn>

                            <RenderIn deviceTypes={[device.TABLET, device.MOBILE]}>
                                <HpSharedCarouselTracking
                                    items={items}
                                    itemsPerPage={1}
                                    onInitialDisplay={() => trackPersonalizationMount()}
                                    onItemsImpression={({ visibleItems, index }) =>
                                        trackPersonalizationImpression({
                                            //Passing visibleItems[0] because itemsPerPage === 1
                                            recommendation: get(visibleItems, '[0].node'),
                                            startIndex: index,
                                        })
                                    }
                                >
                                    {({ handlePageChange }) => (
                                        <Swiper
                                            itemsPerPage={1.2}
                                            classNames={{ list: styles.swiperList }}
                                            onPageChange={handlePageChange}
                                        >
                                            {filterPersonalizationItems(items).map(
                                                (item, index) => (
                                                    <HpRespPersonalizationPanel
                                                        key={item.node.url}
                                                        item={item.node}
                                                        //TODO: if we display more then 1 item per page passing index as pageIndex won't work
                                                        pageIndex={index}
                                                        isVisible
                                                    />
                                                )
                                            )}
                                        </Swiper>
                                    )}
                                </HpSharedCarouselTracking>
                            </RenderIn>
                        </Fragment>
                    ) : (
                        <Spinner containerClass={styles.spinner} />
                    )}
                </div>
            </PageLayout>
        </HpSharedModuleContainer>
    );
}

HpRespPersonalizationModuleComponent.propTypes = {
    personalization: PropTypes.object,
    userType: PropTypes.string,
    userId: PropTypes.string,
};

export const HpRespPersonalizationModule = createFragmentContainer(
    HpRespPersonalizationModuleComponent,
    {
        personalization: graphql`
            fragment HpRespPersonalizationModule_personalization on PersonalizationModuleConnection {
                edges {
                    node {
                        url
                        categoryTitle
                        variant
                        items {
                            serviceId
                            seller {
                                sellerId
                            }
                            mainImage {
                                thumb
                                medium
                            }
                            pdpUrl
                            title
                        }
                    }
                }
            }
        `,
    }
);
